import { Link, useOutletContext } from 'react-router-dom';
import { AlertCircle, BadgeEuro, Save, X } from 'lucide-react';
import { ContactPicker } from '@/components/ContactPicker';
import {
  type BauvorhabenDetailContext,
} from './BauvorhabenDetailContext';

/**
 * Bauvorhaben-Formular: dient der Bearbeiten-Spalte (neben der
 * Leseansicht, Context via useOutletContext) und dem Anlegen
 * (/bauvorhaben/new, Context als Prop — kein Outlet).
 */
export function BauvorhabenEditForm({ context }: { context: BauvorhabenDetailContext }) {
  const {
    formData, setFormData, vertriebler, setVertriebler, users, isNew,
    isSaving, saveError, save, cancel,
  } = context;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
        {isNew ? 'Neues Bauvorhaben' : 'Bauvorhaben bearbeiten'}
      </h2>
      {saveError && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {saveError}
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="label">Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="input"
            required
          />
        </div>
        <div>
          <label className="label">Projekt-Nr.</label>
          <input
            type="text"
            value={formData.project_number}
            onChange={(e) => setFormData({ ...formData, project_number: e.target.value })}
            className="input"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="flex items-center gap-2 text-sm text-gray-900">
            <input
              type="checkbox"
              checked={formData.abgebrochen}
              onChange={(e) => setFormData({ ...formData, abgebrochen: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Abgebrochen
          </label>
          <p className="mt-1 text-xs text-gray-500">
            Der Status (Nicht begonnen / In Arbeit / Abgeschlossen) wird automatisch aus dem Gewerk-Fortschritt berechnet. „Abgebrochen“ setzt das Bauvorhaben manuell auf abgebrochen.
          </p>
        </div>
        <div className="sm:col-span-2">
          <label className="label">Straße</label>
          <input
            type="text"
            value={formData.street}
            onChange={(e) => setFormData({ ...formData, street: e.target.value })}
            className="input"
          />
        </div>
        <div>
          <label className="label">PLZ</label>
          <input
            type="text"
            value={formData.postal_code}
            onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
            className="input"
          />
        </div>
        <div>
          <label className="label">Ort</label>
          <input
            type="text"
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            className="input"
          />
        </div>
        <div>
          <label className="label">Beginn</label>
          <input
            type="date"
            value={formData.start_date}
            onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
            className="input"
          />
        </div>
        <div>
          <label className="label">Ende</label>
          <input
            type="date"
            value={formData.end_date}
            onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
            className="input"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="label">Bauleiter</label>
          <select
            value={formData.bauleiter_user_id}
            onChange={(e) => setFormData({ ...formData, bauleiter_user_id: e.target.value })}
            className="input"
          >
            <option value="">— Kein Bauleiter —</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="label">Vertriebler</label>
          {vertriebler ? (
            <div className="flex items-center gap-2">
              <Link
                to={`/contacts/${vertriebler.id}`}
                onClick={(e) => context.openContactFromLink(e, vertriebler.id, vertriebler.name)}
                className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-800 hover:text-primary-700"
              >
                <BadgeEuro className="h-4 w-4 text-gray-400" />
                {vertriebler.name}
              </Link>
              <button
                type="button"
                onClick={() => setVertriebler(null)}
                className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                title="Vertriebler entfernen"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <ContactPicker
              onSelect={(contact) =>
                setVertriebler({
                  id: contact.id,
                  name: contact.display_name || [contact.first_name, contact.last_name].filter(Boolean).join(' ') || contact.company_name || 'Kontakt',
                })
              }
              placeholder="Vertriebler suchen…"
            />
          )}
        </div>
        <div className="sm:col-span-2">
          <label className="label">Notizen</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={4}
            className="input"
          />
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        <button type="submit" disabled={isSaving} className="btn btn-primary inline-flex items-center gap-2">
          <Save className="h-4 w-4" />
          {isSaving ? 'Speichern…' : 'Speichern'}
        </button>
        <button type="button" onClick={cancel} className="btn btn-secondary">
          Abbrechen
        </button>
      </div>
    </form>
  );
}

/** Route-Element für /bauvorhaben/:id/edit — Context kommt aus dem Layout-Outlet. */
export function BauvorhabenEditRoute() {
  const context = useOutletContext<BauvorhabenDetailContext>();
  return <BauvorhabenEditForm context={context} />;
}